/**
 * Widget de devis en ligne — M. Auto Sud Loire
 * Vanilla JS, zéro dépendance. Se monte sur chaque <div data-mauto-devis-widget>.
 *
 * Parcours en 4 étapes façon ad.fr :
 *   1. Prestation  — barre de recherche/dropdown/icônes ; "Autre" révèle un champ libre
 *      obligatoire immédiatement à cette étape.
 *   2. Véhicule    — immatriculation (format FR validé) + marque + modèle, avec un chip
 *      "VOUS AVEZ SÉLECTIONNÉ : ... ✕" pour changer de prestation sans perdre ces champs.
 *   3. Estimation  — prix selon le mode (fourchette/fixe/à_partir_de) ou message sur_devis,
 *      + premier créneau envisageable (≥72h). Prix connu : deux choix distincts, "Prendre
 *      rendez-vous" (passe la main au widget RDV dédié, cf. handoffToRdv) ou "Demander un
 *      devis" (continue vers l'étape 4 ci-dessous). Sur_devis : uniquement la popup dédiée,
 *      pas de choix (jamais schedulable sans diagnostic préalable).
 *   4. Coordonnées — nom/téléphone/email, bouton final "Demander un devis" (envoie toujours
 *      vers /api/devis — ce flux ne réserve jamais un créneau directement, cf. étape 3).
 * Naviguer en arrière ne perd jamais les champs déjà saisis (conservés dans `state`).
 *
 * Configuration (dans l'ordre de priorité) :
 *   1. attribut `data-api-base` sur le conteneur
 *   2. `window.MAUTO_DEVIS_API_BASE`
 *
 * Pré-remplissage prestation (posé par la section Liquid) :
 *   data-prestation-key="vidange"        → clé exacte d'une prestation → étape 1 sautée
 *   data-prestation-key="freinage"       → nom de "famille" → première variante pré-sélectionnée,
 *                                           étape 1 sautée aussi (dropdown restreint accessible
 *                                           via "← Retour", cf. FAMILIES ci-dessous)
 *   data-prestation-key="climatisation"  → famille "à choix forcé" (cf. FORCE_CHOICE) : affiche
 *                                           les forfaits côte à côte, ne pré-sélectionne rien
 *   (absent)                             → étape 1 complète (dropdown ou barre de recherche)
 *
 * Bloc hero (page d'accueil) :
 *   data-variant="hero"                  → classe CSS mauto-devis--hero
 *   data-quick-picks="vidange,freinage,distribution,climatisation"
 *                                         → rangée d'icônes de raccourci ; cliquer une icône
 *                                           équivaut à chercher/choisir cette prestation
 */
(function () {
  'use strict';

  // Familles : une page prestation peut couvrir plusieurs clés tarifaires.
  // Note : "decalaminage" (VP/utilitaire fusionnés en une seule clé tarifaire depuis la grille
  // 2026) n'est plus une famille — une page data-prestation-key="decalaminage" utilise
  // désormais le chemin isSingle (clé directe), plus isFamily.
  var FAMILIES = {
    freinage: ['plaquettes_av', 'plaquettes_ar', 'disques_plaquettes_av', 'disques_plaquettes_ar'],
    amortisseurs: ['amortisseurs_av', 'amortisseurs_ar'],
    climatisation: ['climatisation_recharge', 'climatisation_entretien'],
  };
  // Libellés d'affichage pour les entrées qui référencent une famille (ex. "freinage")
  // plutôt qu'une clé tarifaire directe (ex. "vidange").
  var FAMILY_LABELS = {
    freinage: 'Freinage',
    amortisseurs: 'Amortisseurs',
    climatisation: 'Climatisation',
  };
  // Familles où l'utilisateur doit explicitement choisir entre les variantes (forfaits
  // distincts, pas de "prestation par défaut" raisonnable) — affichées en cartes côte à côte,
  // jamais pré-sélectionnées automatiquement contrairement aux autres familles.
  var FORCE_CHOICE = { climatisation: true };

  // Doit rester alignée avec la règle serveur (sync-server/server.js, DEVIS_MIN_DELAY_H / nextEligibleSlot).
  // Affichage uniquement — le serveur recalcule et fait foi à la soumission.
  var DEVIS_MIN_DELAY_H = 72;

  function nextEligibleSlot() {
    var d = new Date(Date.now() + DEVIS_MIN_DELAY_H * 3600 * 1000);
    var day = d.getDay(); // 0 = dimanche, 6 = samedi
    if (day === 6) d.setDate(d.getDate() + 2);
    else if (day === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  function formatDateLong(d) {
    try {
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  // Affiche "64,90€" pour un prix avec décimales, "88€" pour un prix rond (pas de ",00" superflu).
  function formatEuro(n) {
    return Number.isInteger(n) ? (n + '€') : (n.toFixed(2).replace('.', ',') + '€');
  }

  // Message unique pour toute prestation en mode sur_devis (y compris "Autre") — cahier des
  // charges définitif : un seul message, pas de variantes selon la raison.
  var SUR_DEVIS_MESSAGE = "Cette intervention nécessite un diagnostic pour vous proposer un prix précis. Laissez-nous votre email, nous vous recontactons sous 24h ouvrées avec un devis chiffré.";

  function formatPrixAffiche(p) {
    if (!p) return '';
    var base;
    if (p.mode === 'fixe') base = formatEuro(p.prix_fixe) + ' TTC';
    else if (p.mode === 'sur_devis') return SUR_DEVIS_MESSAGE;
    else if (p.min != null && p.max != null) base = 'Entre ' + formatEuro(p.min) + ' et ' + formatEuro(p.max) + ' TTC';
    else if (p.a_partir_de != null) base = 'À partir de ' + formatEuro(p.a_partir_de) + ' TTC';
    else return '';
    if (p.supplement_recent != null) {
      base += ' (+' + formatEuro(p.supplement_recent) + ' pour véhicules 2013 ou plus récents)';
    }
    return base;
  }

  // Prestation "Autre" : pas une clé tarifaire réelle (jamais renvoyée par /api/pricing),
  // injectée côté client à la fin de la liste sélectionnable. Toujours sur_devis.
  var AUTRE_KEY = 'autre';
  var AUTRE_PRESTATION = { label: 'Autre (précisez)', mode: 'sur_devis' };

  // Liste "générale" (barre de recherche + dropdown complet de /pages/devis-en-ligne) — toutes
  // les prestations de la grille tarifaire, dans l'ordre de DEFAULT_PRICING (sync-server/server.js).
  // N'affecte pas les pages prestation dédiées (familles ou clé unique), qui gardent leur propre
  // périmètre inchangé.
  var GENERAL_LIST_KEYS = [
    'vidange', 'revision', 'controle_technique', 'plaquettes_av', 'plaquettes_ar',
    'disques_plaquettes_av', 'disques_plaquettes_ar', 'distribution', 'embrayage',
    'amortisseurs_av', 'amortisseurs_ar', 'geometrie', 'climatisation_recharge',
    'climatisation_entretien', 'batterie', 'pneus', 'diagnostic', 'decalaminage',
  ];

  // Dimension pneu obligatoire — déclenché uniquement pour la clé tarifaire "pneus" (doit
  // rester aligné avec DEVIS_PNEU_KEY côté sync-server/server.js). Mêmes listes et même
  // logique que le widget "Prendre RDV" (devis-rdv-widget.js) — dupliqué ici car les deux
  // widgets sont des fichiers/IIFE totalement indépendants, sans état partagé.
  var DEVIS_PNEU_KEY = 'pneus';
  var NE_SAIT_PAS = 'ne_sait_pas';
  var PNEU_LARGEURS = ['175', '185', '195', '205', '215', '225', '235', '245'];
  var PNEU_LARGEURS_TOUTES = ['135', '145', '155', '165', '175', '185', '195', '205', '215', '225', '235', '245', '255', '265', '275', '285', '295', '305', '315'];
  var PNEU_HAUTEURS = ['35', '40', '45', '50', '55', '60', '65', '70', '75', '80', '82'];
  // Ces 6 listes (largeurs/hauteurs/diamètres/charges/vitesses/saisons) doivent rester strictement
  // identiques aux mêmes constantes dans devis-rdv-widget.js (tiroir pneu dupliqué, pas partagé) —
  // sinon un client peut chercher côté devis une dimension indisponible côté RDV direct, ou l'inverse.
  var PNEU_DIAMETRES = ['14', '15', '16', '17', '18', '19'];
  var PNEU_CHARGES = ['89', '93'];
  var PNEU_VITESSES = ['H', 'V', 'W', 'Y', 'Z'];
  var PNEU_SAISONS = [
    { key: 'ete', label: 'Été', icon: '☀️' },
    { key: '4saisons', label: '4 saisons', icon: '🌦️' },
    { key: 'hiver', label: 'Hiver', icon: '❄️' },
    { key: 'toutes', label: 'Toutes', icon: '🌈' },
  ];
  function pneuSaisonLabel(key) {
    var found = PNEU_SAISONS.filter(function (s) { return s.key === key; })[0];
    return found ? found.label : '';
  }

  // Partie 3 — une fois le tiroir dimension complété, le parcours ne continue plus dans ce
  // widget (pneus n'a jamais eu de vrai prix chiffrable en ligne, cf. l'ancien popup sur_devis) :
  // il redirige vers la page résultats (collection "Pneus" filtrée par dimension), cf.
  // endPneuFlow. L'URL utilise des query params (pas de vrai slug, contrainte Shopify) — le nom
  // des paramètres doit rester aligné avec assets/pneus-resultats.js.
  var PNEU_RESULTATS_URL = '/collections/pneus';
  function buildPneuResultsUrl(pneu) {
    var params = new URLSearchParams();
    if (pneu.largeur) params.set('largeur', pneu.largeur);
    if (pneu.hauteur) params.set('hauteur', pneu.hauteur);
    if (pneu.diametre) params.set('diametre', pneu.diametre);
    var saisonLabel = pneuSaisonLabel(pneu.saison);
    if (saisonLabel) params.set('saison', saisonLabel);
    if (pneu.quantite) params.set('quantite', pneu.quantite);
    return PNEU_RESULTATS_URL + '?' + params.toString();
  }

  // Clé localStorage utilisée pour transmettre un item "Ajouter" de la page résultats pneus
  // (page Shopify séparée, cf. assets/pneus-resultats.js) jusqu'à ce widget, seul endroit où vit
  // réellement state.cart — doit rester strictement identique dans les deux fichiers.
  var PNEU_CART_HANDOFF_KEY = 'mautoPendingPneuCartItem';
  function consumePendingPneuCartItem() {
    var raw;
    try { raw = window.localStorage.getItem(PNEU_CART_HANDOFF_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { window.localStorage.removeItem(PNEU_CART_HANDOFF_KEY); } catch (e) { /* ignoré */ }
    try {
      var item = JSON.parse(raw);
      return (item && item.type === 'pneu_produit') ? item : null;
    } catch (e) {
      return null;
    }
  }

  // Montant chiffré d'un item pneu_produit (toujours chiffré, jamais sur_devis) : (prix pneu +
  // prix montage) unitaires × quantité. Mêmes unités que la page résultats (déjà en euros, pas
  // en centimes — conversion faite une fois pour toutes côté assets/pneus-resultats.js).
  function pneuProduitMontant(item) {
    var qte = parseInt(item.quantite, 10) || 0;
    var unitaire = (Number(item.prixPneuUnitaire) || 0) + (Number(item.prixMontageUnitaire) || 0);
    return unitaire * qte;
  }

  // Immatriculation française format SIV (ex. AB-123-CD) — tirets ou espaces optionnels.
  var IMMAT_REGEX = /^[A-Za-z]{2}[\s-]?\d{3}[\s-]?[A-Za-z]{2}$/;
  function normalizeImmat(v) {
    var compact = v.replace(/[\s-]/g, '').toUpperCase();
    if (compact.length === 7) return compact.slice(0, 2) + '-' + compact.slice(2, 5) + '-' + compact.slice(5, 7);
    return v.trim().toUpperCase();
  }

  // Icônes de raccourci pour le bloc hero (page d'accueil) — cf. data-quick-picks.
  var ICONS = {
    vidange: '🛢️', freinage: '🛑', distribution: '⚙️', climatisation: '❄️',
    revision: '🔩', controle_technique: '✅', embrayage: '🔧', geometrie: '📐',
    diagnostic: '🔍', decalaminage: '🧪', batterie: '🔋', pneus: '🛞',
  };
  function iconFor(key) { return ICONS[key] || '🔧'; }

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function normalize(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  function el(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  function getApiBase(container) {
    return container.getAttribute('data-api-base') || window.MAUTO_DEVIS_API_BASE || '';
  }

  function initWidget(container) {
    var apiBase = getApiBase(container);
    if (!apiBase) {
      container.innerHTML = '<p class="mauto-devis-error">Widget de devis non configuré (API manquante).</p>';
      return;
    }

    var presetKey = container.getAttribute('data-prestation-key') || '';
    var isFamily = !!FAMILIES[presetKey];
    var isSingle = !isFamily && !!presetKey;

    var quickPicks = (container.getAttribute('data-quick-picks') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var variant = container.getAttribute('data-variant') || '';
    var useSearchBar = variant === 'hero';

    // État persistant du parcours — jamais réinitialisé en cours de route, pour que "← Retour"
    // ne fasse jamais perdre ce qui a déjà été saisi.
    var state = {
      pricing: null,
      prestationKey: '',
      autreDescription: '',
      vehicule: { immatriculation: '', marque: '', modele: '' },
      coordonnees: { nom: '', telephone: '', email: '' },
      pneu: { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' },
      // Panier (Partie 2, ad.fr-style) : prestations déjà "ajoutées" via le bouton dédié à
      // l'étape Estimation, en plus de celle en cours (prestationKey/pneu/autreDescription
      // ci-dessus). Vide = comportement mono-prestation strictement inchangé (même payload,
      // même endpoint /api/devis). Le véhicule et les coordonnées restent partagés par tout le
      // panier — jamais redemandés d'un item à l'autre.
      cart: [],
      step: 1,
    };
    // Récupère un éventuel item pneu ajouté depuis la page résultats (Partie 3) avant ce montage
    // — une seule fois : consumePendingPneuCartItem() vide la clé localStorage dès la lecture,
    // donc un second widget monté sur la même page (ex. le widget flottant après le widget de
    // page) ne le récupère pas une deuxième fois.
    var pendingPneuItem = consumePendingPneuCartItem();
    if (pendingPneuItem) state.cart.push(pendingPneuItem);
    // Où renvoie "← Retour" depuis l'étape Véhicule — dépend du contexte (recherche libre,
    // dropdown scopé à une famille, cartes de forfaits...). null = pas de bouton retour
    // (page prestation à clé unique, rien à choisir d'autre).
    var goBackFromVehicule = null;
    // Sur mobile, le bloc hero affiche d'abord un simple bouton "Choisir votre prestation" à la
    // place de la barre de recherche — une fois cliqué (heroEntered = true), la vraie étape 1
    // (recherche + autocomplétion) s'affiche en plein écran comme les autres étapes, et le reste
    // du parcours (y compris un retour ultérieur à l'étape 1) ne revoit plus jamais ce bouton
    // compact pour cette instance du widget. Desktop inchangé (jamais concerné, cf. usage plus bas).
    var heroEntered = false;

    container.innerHTML = '';
    container.classList.add('mauto-devis');
    if (variant) container.classList.add('mauto-devis--' + variant);

    // Mode plein écran mobile (<768px, cf. devis-widget.css) : la carte flottante devient un
    // overlay fixed couvrant tout le viewport. Sur la variante "hero" (page d'accueil), le bloc
    // hero (image/titre/barre de recherche) reste visuellement inchangé tant qu'aucune
    // prestation n'est choisie — le plein écran n'active qu'à partir de ce moment-là (cf.
    // setFlowActive). Sur les autres pages (dropdown intégré à la page), il n'y a pas de "bloc
    // hero" à préserver : le plein écran est actif en permanence, dès le montage.
    var currentBackHandler = null;
    function setFlowActive(active) {
      if (!useSearchBar) return;
      container.classList.toggle('mauto-devis-flow-active', active);
      document.body.classList.toggle('mauto-devis-scroll-lock', active);
    }
    if (!useSearchBar) container.classList.add('mauto-devis-flow-active');

    var mobileTopbar = el(
      '<div class="mauto-devis-mobile-topbar">' +
        '<button type="button" class="mauto-devis-mobile-back is-invisible" aria-label="Retour">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
        '</button>' +
        '<div class="mauto-devis-mobile-progress">' +
          '<span class="mauto-devis-mobile-dot" data-n="1"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="2"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="3"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="4"></span>' +
        '</div>' +
      '</div>'
    );
    container.appendChild(mobileTopbar);
    var mobileBackBtn = mobileTopbar.querySelector('.mauto-devis-mobile-back');
    mobileBackBtn.addEventListener('click', function () { if (currentBackHandler) currentBackHandler(); });

    var stepsBar = el(
      '<div class="mauto-devis-stepsbar">' +
        '<span class="mauto-devis-stepdot" data-n="1">1. Prestation</span>' +
        '<span class="mauto-devis-stepdot" data-n="2">2. Véhicule</span>' +
        '<span class="mauto-devis-stepdot" data-n="3">3. Estimation</span>' +
        '<span class="mauto-devis-stepdot" data-n="4">4. Coordonnées</span>' +
      '</div>'
    );
    container.appendChild(stepsBar);

    var quickPicksBar = null;
    if (quickPicks.length) {
      quickPicksBar = el('<div class="mauto-devis-quickpicks"></div>');
      container.appendChild(quickPicksBar);
    }

    // Barre panier persistante (Partie 2) — masquée tant que state.cart est vide, donc invisible
    // sur tout parcours mono-prestation classique. Reste affichée et interactive (retrait d'un
    // item) sur toutes les étapes, y compris Coordonnées, sans dupliquer son rendu ailleurs.
    var cartBar = el('<div class="mauto-devis-cart-bar" hidden></div>');
    container.appendChild(cartBar);

    var body = el('<div class="mauto-devis-body"></div>');
    container.appendChild(body);

    function cartItemLabel(item) {
      if (item.type === 'pneu_produit') {
        return (item.marque || '') + ' ' + (item.modele || '') + ' (pneus ' + (item.dimension || '') + ')';
      }
      var p = item.prestationKey === AUTRE_KEY ? AUTRE_PRESTATION : state.pricing[item.prestationKey];
      return p ? p.label : item.prestationKey;
    }

    // Jusqu'au bout du panier sans passer par une autre prestation : efface toute sélection en
    // cours (jamais "ajoutée" donc jamais comptée) avant de sauter directement aux Coordonnées —
    // utile en particulier pour un panier composé uniquement de pneus (results page), qui n'a
    // jamais besoin de choisir de prestation classique.
    function validateCartOnly() {
      state.prestationKey = '';
      state.autreDescription = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      // Un panier composé uniquement de pneus n'est jamais passé par l'étape Véhicule (le tiroir
      // pneu redirige directement vers la page résultats, cf. endPneuFlow) — la demander ici une
      // seule fois si elle manque encore, sinon (déjà connue via une autre prestation) filer
      // directement aux Coordonnées.
      var vehiculeKnown = state.vehicule.immatriculation && state.vehicule.marque && state.vehicule.modele;
      if (vehiculeKnown) {
        goToStep(4);
      } else {
        goBackFromVehicule = renderStep1;
        goToStep(2);
      }
    }

    function renderCartBar() {
      if (!state.cart.length) {
        cartBar.hidden = true;
        cartBar.innerHTML = '';
        return;
      }
      cartBar.hidden = false;
      var itemsHtml = state.cart.map(function (item, idx) {
        return '<span class="mauto-devis-cart-item">' + esc(cartItemLabel(item)) +
          '<button type="button" class="mauto-devis-cart-remove" data-idx="' + idx + '" aria-label="Retirer cette prestation">✕</button></span>';
      }).join('');
      cartBar.innerHTML =
        '<span class="mauto-devis-cart-label">🛒 ' + state.cart.length + ' prestation' + (state.cart.length > 1 ? 's' : '') + ' ajoutée' + (state.cart.length > 1 ? 's' : '') + '</span>' +
        '<span class="mauto-devis-cart-items">' + itemsHtml + '</span>' +
        (state.step !== 4 ? '<button type="button" class="mauto-devis-cart-validate" data-action="valider-panier">Valider mon panier →</button>' : '');
      var removeBtns = cartBar.querySelectorAll('.mauto-devis-cart-remove');
      for (var i = 0; i < removeBtns.length; i++) {
        removeBtns[i].addEventListener('click', function (ev) {
          state.cart.splice(Number(ev.currentTarget.getAttribute('data-idx')), 1);
          renderCartBar();
        });
      }
      var validateBtn = cartBar.querySelector('[data-action="valider-panier"]');
      if (validateBtn) validateBtn.addEventListener('click', validateCartOnly);
    }

    // `backHandler` : fonction appelée par la flèche de retour de la barre fixe mobile
    // (`.mauto-devis-mobile-back`) — null/absent = pas de retour possible depuis cette étape
    // (flèche rendue invisible mais garde sa place pour ne pas déséquilibrer la barre).
    function setStep(n, backHandler) {
      state.step = n;
      currentBackHandler = backHandler || null;
      mobileBackBtn.classList.toggle('is-invisible', !currentBackHandler);
      var dots = stepsBar.querySelectorAll('.mauto-devis-stepdot');
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', Number(dots[i].getAttribute('data-n')) === n);
        dots[i].classList.toggle('is-done', Number(dots[i].getAttribute('data-n')) < n);
      }
      var mdots = mobileTopbar.querySelectorAll('.mauto-devis-mobile-dot');
      for (var j = 0; j < mdots.length; j++) {
        mdots[j].classList.toggle('is-active', Number(mdots[j].getAttribute('data-n')) === n);
        mdots[j].classList.toggle('is-done', Number(mdots[j].getAttribute('data-n')) < n);
      }
      renderCartBar();
    }

    // Ajoute la prestation en cours au panier et repart sur l'étape 1 pour la suivante. Le
    // véhicule (déjà saisi) et les coordonnées ne sont jamais redemandés — cf. goToVehiculeOrEstimation.
    function addCurrentToCart() {
      state.cart.push({
        prestationKey: state.prestationKey,
        pneu: state.prestationKey === DEVIS_PNEU_KEY ? JSON.parse(JSON.stringify(state.pneu)) : null,
        message: state.prestationKey === AUTRE_KEY ? state.autreDescription : '',
      });
      state.prestationKey = '';
      state.autreDescription = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      renderStep1();
    }

    // Point de passage unique après le choix d'une prestation : la toute première passe par
    // l'étape Véhicule comme aujourd'hui ; à partir de la 2e (panier non vide, véhicule déjà
    // connu), l'étape Véhicule est sautée — direction l'Estimation.
    function goToVehiculeOrEstimation() {
      goToStep(state.cart.length > 0 ? 3 : 2);
    }

    // Symétrique de goToVehiculeOrEstimation pour le "← Retour" depuis l'Estimation : si l'étape
    // Véhicule a été sautée à l'aller (panier non vide), le retour va au choix de prestation (1)
    // plutôt qu'à une étape Véhicule jamais affichée pour cet item.
    function backFromEstimation() {
      goToStep(state.cart.length > 0 ? 1 : 2);
    }

    function goToStep(n) {
      if (n === 1) renderStep1();
      else if (n === 2) renderStepVehicule();
      else if (n === 3) renderStepEstimation();
      else if (n === 4) renderStepCoordonnees();
    }

    function renderLoading() {
      body.innerHTML = '<p class="mauto-devis-loading">Chargement des tarifs…</p>';
    }

    function renderError(msg) {
      body.innerHTML = '<p class="mauto-devis-error">' + esc(msg) + '</p>';
    }

    // Liste "plate" des entrées sélectionnables pour la barre de recherche : une entrée par
    // famille (libellé générique, ex. "Freinage") + une entrée par clé tarifaire qui n'appartient
    // à aucune famille (ex. "vidange").
    function buildSuggestionList() {
      var list = [];
      GENERAL_LIST_KEYS.forEach(function (k) {
        if (state.pricing[k]) list.push({ key: k, label: state.pricing[k].label });
      });
      list.push({ key: AUTRE_KEY, label: AUTRE_PRESTATION.label });
      return list;
    }

    // Résout une entrée choisie (clé directe, nom de famille, ou "Autre") et avance vers
    // l'étape appropriée. Point d'entrée unique utilisé par le dropdown, la barre de
    // recherche et les icônes de raccourci.
    function selectSuggestion(key) {
      if (key === AUTRE_KEY) {
        renderAutreStep();
        return;
      }
      if (FAMILIES[key]) {
        if (FORCE_CHOICE[key]) {
          renderFamilyChoice(key, true);
        } else {
          state.prestationKey = FAMILIES[key][0];
          goBackFromVehicule = renderStep1;
          goToVehiculeOrEstimation();
        }
      } else {
        state.prestationKey = key;
        goBackFromVehicule = renderStep1;
        if (key === DEVIS_PNEU_KEY) startPneuFlow(goToVehiculeOrEstimation);
        else goToVehiculeOrEstimation();
      }
    }

    // ─── Tiroir dimension pneu (6 étapes obligatoires, sauf la dernière) ─────────────────────
    // Déclenché uniquement quand la clé tarifaire choisie est DEVIS_PNEU_KEY, avant l'étape
    // Véhicule. `onDone` : callback fourni par l'appelant (selectSuggestion ou le pré-remplissage
    // isSingle) — permet de reprendre le parcours normal (goToStep(2)) quel que soit le point
    // d'entrée. Réutilise les mêmes classes .mauto-pneu-* que devis-rdv-widget.css (partagées,
    // pas dupliquées) et le même principe : mobileTopbar/stepsBar masqués pendant le sous-parcours.
    function pneuSummaryText() {
      var p = state.pneu;
      if (!p.largeur) return '';
      var s = p.largeur;
      if (p.hauteur) s += '/' + p.hauteur;
      if (p.diametre) s += ' R' + p.diametre;
      if (p.indiceCharge) s += ' ' + (p.indiceCharge === NE_SAIT_PAS ? '?' : p.indiceCharge);
      if (p.indiceVitesse) s += (p.indiceVitesse === NE_SAIT_PAS ? '?' : p.indiceVitesse);
      return s;
    }

    function startPneuFlow(onDone) {
      setFlowActive(true);
      mobileTopbar.style.display = 'none';
      stepsBar.style.display = 'none';
      if (quickPicksBar) quickPicksBar.style.display = 'none';
      renderPneuStepLargeur(onDone);
    }

    // Partie 3 : le tiroir ne débouche plus sur l'étape Véhicule/Estimation de ce widget (pneus
    // n'a jamais eu de prix chiffrable ici, seulement l'ancien popup sur_devis) — il redirige
    // vers la page résultats pneus. `onDone` (toujours fourni par les appelants, cf. startPneuFlow)
    // n'est donc plus appelé ; conservé dans la signature uniquement pour ne pas devoir modifier
    // tous les points d'appel qui le passent encore.
    function endPneuFlow(onDone) {
      window.location.href = buildPneuResultsUrl(state.pneu);
    }

    function cancelPneuFlow() {
      mobileTopbar.style.display = '';
      stepsBar.style.display = '';
      if (quickPicksBar) quickPicksBar.style.display = '';
      state.prestationKey = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      renderStep1();
    }

    function renderPneuChrome(n, title, backHandler) {
      var pct = Math.round((n / 6) * 100);
      var summary = pneuSummaryText();
      return (
        '<div class="mauto-pneu-header">' +
          (backHandler ? '<button type="button" class="mauto-pneu-back" data-action="pneu-back" aria-label="Retour"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>' : '<span class="mauto-pneu-back-spacer"></span>') +
          '<span class="mauto-pneu-title">Trouver mes pneus</span>' +
          '<button type="button" class="mauto-pneu-close" data-action="pneu-close" aria-label="Fermer">✕</button>' +
        '</div>' +
        (summary ? '<div class="mauto-pneu-recap"><span>Dimension : ' + esc(summary) + '</span><button type="button" class="mauto-pneu-edit" data-action="pneu-edit" aria-label="Modifier la dimension">✎</button></div>' : '') +
        '<div class="mauto-pneu-progress-row"><span>' + esc(title) + '</span><span>' + n + '/6</span></div>' +
        '<div class="mauto-pneu-progressbar"><div style="width:' + pct + '%"></div></div>'
      );
    }

    function wirePneuChrome(backHandler, onDone) {
      // Bug iOS Safari (position:fixed + -webkit-overflow-scrolling:touch, cf. devis-widget.css) :
      // après un remplacement de body.innerHTML, la barre de progression du tiroir pneu peut
      // rester peinte "détachée" en bas de l'écran physique au lieu de sa vraie position dans le
      // flux — le translateZ(0) posé sur .mauto-devis-body ne suffit pas seul dans tous les cas
      // (captures utilisateur iPhone, 2026-08-23). Remettre le scroll en haut et forcer un reflow
      // synchrone à chaque étape corrige le repaint.
      if (window.innerWidth <= 768) {
        body.scrollTop = 0;
        void body.offsetHeight;
      }
      var backBtn = body.querySelector('[data-action="pneu-back"]');
      if (backBtn && backHandler) backBtn.addEventListener('click', backHandler);
      body.querySelector('[data-action="pneu-close"]').addEventListener('click', cancelPneuFlow);
      var editBtn = body.querySelector('[data-action="pneu-edit"]');
      if (editBtn) editBtn.addEventListener('click', function () { renderPneuStepLargeur(onDone); });
      var infoBtn = body.querySelector('[data-action="pneu-info"]');
      if (infoBtn) {
        infoBtn.addEventListener('click', function () {
          var t = body.querySelector('.mauto-pneu-info-text');
          t.hidden = !t.hidden;
        });
      }
    }

    function wirePneuOptions(field, onPick) {
      var btns = body.querySelectorAll('.mauto-pneu-option');
      for (var i = 0; i < btns.length; i++) {
        btns[i].addEventListener('click', function (ev) {
          state.pneu[field] = ev.currentTarget.getAttribute('data-value');
          onPick();
        });
      }
    }

    function renderPneuStepLargeur(onDone) {
      var showAll = !!state._pneuShowAllLargeurs;
      var list = showAll ? PNEU_LARGEURS_TOUTES : PNEU_LARGEURS;
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">ℹ️ Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La largeur, la hauteur et le diamètre sont inscrits sur le flanc du pneu (ex&nbsp;: 205/55 R16) ou dans le carnet d\'entretien du véhicule.</p>' +
        '<p class="mauto-devis-field-label">Largeurs les plus recherchées</p>' +
        '<div class="mauto-pneu-grid">' +
          list.map(function (v) {
            return '<button type="button" class="mauto-pneu-option' + (state.pneu.largeur === v ? ' is-selected' : '') + '" data-value="' + v + '">' + v + '</button>';
          }).join('') +
        '</div>' +
        (!showAll ? '<button type="button" class="mauto-pneu-link" data-action="pneu-toggle-all">Voir toutes les largeurs</button>' : '');
      wirePneuChrome(null, onDone);
      var toggleAll = body.querySelector('[data-action="pneu-toggle-all"]');
      if (toggleAll) {
        toggleAll.addEventListener('click', function () {
          state._pneuShowAllLargeurs = true;
          renderPneuStepLargeur(onDone);
        });
      }
      wirePneuOptions('largeur', function () { renderPneuStepHauteur(onDone); });
    }

    function renderPneuStepHauteur(onDone) {
      body.innerHTML =
        renderPneuChrome(2, 'Hauteur', function () { renderPneuStepLargeur(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">ℹ️ Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La hauteur (en % de la largeur) est le deuxième nombre inscrit sur le flanc du pneu (ex&nbsp;: 205/<strong>55</strong> R16).</p>' +
        '<div class="mauto-pneu-grid">' +
          PNEU_HAUTEURS.map(function (v) {
            return '<button type="button" class="mauto-pneu-option' + (state.pneu.hauteur === v ? ' is-selected' : '') + '" data-value="' + v + '">' + v + '</button>';
          }).join('') +
        '</div>';
      wirePneuChrome(function () { renderPneuStepLargeur(onDone); }, onDone);
      wirePneuOptions('hauteur', function () { renderPneuStepDiametre(onDone); });
    }

    function renderPneuStepDiametre(onDone) {
      body.innerHTML =
        renderPneuChrome(3, 'Diamètre', function () { renderPneuStepHauteur(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">ℹ️ Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Le diamètre (en pouces) est inscrit après le "R" sur le flanc du pneu (ex&nbsp;: 205/55 R<strong>16</strong>) — c\'est aussi le diamètre de la jante.</p>' +
        '<div class="mauto-pneu-grid">' +
          PNEU_DIAMETRES.map(function (v) {
            return '<button type="button" class="mauto-pneu-option' + (state.pneu.diametre === v ? ' is-selected' : '') + '" data-value="' + v + '">' + v + '</button>';
          }).join('') +
        '</div>';
      wirePneuChrome(function () { renderPneuStepHauteur(onDone); }, onDone);
      wirePneuOptions('diametre', function () { renderPneuStepCharge(onDone); });
    }

    function renderPneuIndiceStep(n, title, values, field, nextFn, backFn, guideTitle, guideBody, riskList, onDone) {
      body.innerHTML =
        renderPneuChrome(n, title, backFn) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">ℹ️ Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Cette information est inscrite sur le flanc du pneu juste après la dimension (ex&nbsp;: 205/55 R16 <strong>91V</strong>) ou dans le carnet d\'entretien.</p>' +
        '<div class="mauto-pneu-grid">' +
          values.map(function (v) {
            return '<button type="button" class="mauto-pneu-option' + (state.pneu[field] === v ? ' is-selected' : '') + '" data-value="' + v + '">' + v + '</button>';
          }).join('') +
        '</div>' +
        '<button type="button" class="mauto-pneu-link" data-action="pneu-ne-sait-pas">Je ne sais pas</button>' +
        '<div class="mauto-pneu-guide">' +
          '<button type="button" class="mauto-pneu-guide-toggle" data-action="pneu-guide-toggle">Guide d\'achat <span>▾</span></button>' +
          '<div class="mauto-pneu-guide-body" hidden>' +
            '<p><strong>' + esc(guideTitle) + '</strong></p><p>' + guideBody + '</p>' +
            '<p><strong>Risque en cas d\'erreur&nbsp;:</strong></p>' +
            '<ul>' + riskList.map(function (r) { return '<li>' + esc(r) + '</li>'; }).join('') + '</ul>' +
          '</div>' +
        '</div>';
      wirePneuChrome(backFn, onDone);
      wirePneuOptions(field, nextFn);
      body.querySelector('[data-action="pneu-ne-sait-pas"]').addEventListener('click', function () {
        state.pneu[field] = NE_SAIT_PAS;
        nextFn();
      });
      body.querySelector('[data-action="pneu-guide-toggle"]').addEventListener('click', function () {
        var g = body.querySelector('.mauto-pneu-guide-body');
        g.hidden = !g.hidden;
      });
    }

    function renderPneuStepCharge(onDone) {
      renderPneuIndiceStep(4, 'Indice de charge', PNEU_CHARGES, 'indiceCharge',
        function () { renderPneuStepVitesse(onDone); },
        function () { renderPneuStepDiametre(onDone); },
        'Indice de charge',
        'Plus il est élevé, plus la charge maximale supportée augmente. Il est obligatoire d\'utiliser des pneus dont l\'indice est égal ou supérieur aux préconisations constructeur (carnet d\'entretien). Vous pouvez monter d\'un ou plusieurs indices, mais jamais en dessous de la préconisation.',
        ['Risque de déjantage', 'Risque d\'éclatement du pneumatique'], onDone);
    }

    function renderPneuStepVitesse(onDone) {
      renderPneuIndiceStep(5, 'Indice de vitesse', PNEU_VITESSES, 'indiceVitesse',
        function () { renderPneuStepSaison(onDone); },
        function () { renderPneuStepCharge(onDone); },
        'Indice de vitesse',
        'Il indique la vitesse maximale supportée par le pneu. Comme pour l\'indice de charge, il ne doit jamais être inférieur à la préconisation constructeur — vous pouvez en revanche monter en gamme.',
        ['Perte d\'adhérence', 'Risque d\'éclatement suite à une surchauffe', 'Véhicule refusé au contrôle technique'], onDone);
    }

    function renderPneuStepSaison(onDone) {
      var qte = state.pneu.quantite;
      var qteIsCustom = !!qte && qte !== '2' && qte !== '4';
      var qteCustomVisible = qteIsCustom || state._qteAutreOpen;
      body.innerHTML =
        renderPneuChrome(6, 'Paramètres', function () { renderPneuStepVitesse(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">ℹ️ Quels pneus pour quelle saison ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Été&nbsp;: meilleure tenue de route par temps sec/chaud. Hiver&nbsp;: adhérence en dessous de 7°C, neige/verglas. 4 saisons&nbsp;: compromis toute l\'année sans changer de jeu.</p>' +
        '<p class="mauto-devis-field-label">Sélectionnez la saison de vos pneus</p>' +
        '<div class="mauto-pneu-saison-grid">' +
          PNEU_SAISONS.map(function (s) {
            return '<button type="button" class="mauto-pneu-saison' + (state.pneu.saison === s.key ? ' is-selected' : '') + '" data-value="' + s.key + '">' +
              '<span class="mauto-pneu-saison-icon">' + s.icon + '</span><span>' + esc(s.label) + '</span></button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-devis-field-label">Nombre de pneus à changer</p>' +
        '<div class="mauto-pneu-grid">' +
          '<button type="button" class="mauto-pneu-option' + (qte === '2' ? ' is-selected' : '') + '" data-qte="2">2 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qte === '4' ? ' is-selected' : '') + '" data-qte="4">4 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qteCustomVisible ? ' is-selected' : '') + '" data-qte="autre">Autre</button>' +
        '</div>' +
        (qteCustomVisible ?
          '<div class="mauto-devis-field mauto-pneu-qte-custom">' +
            '<input type="number" min="1" max="8" inputmode="numeric" placeholder="Nombre de pneus" value="' + (qteIsCustom ? esc(qte) : '') + '">' +
          '</div>'
        : '') +
        '<p class="mauto-devis-field-label">Spécificité véhicule</p>' +
        '<label class="mauto-pneu-toggle-row">' +
          '<span>Véhicule électrique</span>' +
          '<input type="checkbox" class="mauto-pneu-ve-toggle"' + (state.pneu.vehiculeElectrique ? ' checked' : '') + '>' +
        '</label>' +
        '<div class="mauto-devis-actions mauto-pneu-actions-stacked">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-secondary" data-action="pneu-skip"' + (state.pneu.quantite ? '' : ' disabled') + '>Passer cette étape</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pneu-valider"' + (state.pneu.saison && state.pneu.quantite ? '' : ' disabled') + '>Valider</button>' +
        '</div>';
      wirePneuChrome(function () { renderPneuStepVitesse(onDone); }, onDone);
      var validerBtn = body.querySelector('[data-action="pneu-valider"]');
      var skipBtn = body.querySelector('[data-action="pneu-skip"]');
      var saisonBtns = body.querySelectorAll('.mauto-pneu-saison');
      for (var i = 0; i < saisonBtns.length; i++) {
        saisonBtns[i].addEventListener('click', function (ev) {
          state.pneu.saison = ev.currentTarget.getAttribute('data-value');
          var all = body.querySelectorAll('.mauto-pneu-saison');
          for (var j = 0; j < all.length; j++) all[j].classList.toggle('is-selected', all[j] === ev.currentTarget);
          validerBtn.disabled = !state.pneu.quantite;
        });
      }
      var qteBtns = body.querySelectorAll('[data-qte]');
      for (var k = 0; k < qteBtns.length; k++) {
        qteBtns[k].addEventListener('click', function (ev) {
          var v = ev.currentTarget.getAttribute('data-qte');
          if (v === 'autre') {
            state._qteAutreOpen = true;
            state.pneu.quantite = '';
          } else {
            state._qteAutreOpen = false;
            state.pneu.quantite = v;
          }
          renderPneuStepSaison(onDone);
        });
      }
      var qteCustomInput = body.querySelector('.mauto-pneu-qte-custom input');
      if (qteCustomInput) {
        qteCustomInput.focus();
        qteCustomInput.addEventListener('input', function (ev) {
          state.pneu.quantite = ev.target.value.trim();
          skipBtn.disabled = !state.pneu.quantite;
          validerBtn.disabled = !(state.pneu.saison && state.pneu.quantite);
        });
      }
      body.querySelector('.mauto-pneu-ve-toggle').addEventListener('change', function (ev) {
        state.pneu.vehiculeElectrique = ev.target.checked;
      });
      skipBtn.addEventListener('click', function () { endPneuFlow(onDone); });
      validerBtn.addEventListener('click', function () { endPneuFlow(onDone); });
    }

    function renderDropdownStep() {
      setStep(1, null);
      var keys = isFamily ? FAMILIES[presetKey] : GENERAL_LIST_KEYS.concat([AUTRE_KEY]);
      var options = '<option value="">— Choisissez une prestation —</option>';
      keys.forEach(function (k) {
        var p = state.pricing[k];
        if (!p) return;
        options += '<option value="' + esc(k) + '">' + esc(p.label) + '</option>';
      });
      body.innerHTML =
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-prestation">Quelle prestation vous intéresse ?</label>' +
          '<select id="mauto-devis-prestation" class="mauto-devis-select">' + options + '</select>' +
        '</div>';
      var select = body.querySelector('#mauto-devis-prestation');
      if (state.prestationKey) select.value = state.prestationKey;
      // Sur desktop, les flèches clavier (select focus + fermé) ou la molette au survol
      // déclenchent un "change" à CHAQUE incrément, pas seulement au choix final — sans ce
      // léger débounce, l'assistant avance dès la première valeur traversée (ex. l'option
      // juste après le choix précédent) et l'utilisateur n'atteint jamais "Pneus (achat et
      // pose)" en descendant la liste. Le picker natif mobile ne déclenche qu'un seul "change"
      // final, d'où l'absence du bug sur mobile (cf. bug rapporté 2026-08-24). 180ms est
      // imperceptible pour un clic/sélection normale mais absorbe une rafale clavier/molette.
      var prestationChangeTimer = null;
      select.addEventListener('change', function () {
        clearTimeout(prestationChangeTimer);
        prestationChangeTimer = setTimeout(function () {
          if (select.value) selectSuggestion(select.value);
        }, 180);
      });
    }

    // Barre de recherche + autocomplétion + CTA "Faire un devis" (variante hero uniquement).
    function renderSearchStep() {
      // Sur mobile, tant que le visiteur n'a pas cliqué le bouton compact, le bloc hero garde
      // son affichage réduit — pas de plein écran, rien à faire ici avant ce clic.
      if (variant === 'hero' && !heroEntered && window.matchMedia('(max-width: 768px)').matches) {
        setStep(1, null);
        body.innerHTML = '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary mauto-devis-hero-start-btn">Choisir votre prestation</button>';
        body.querySelector('.mauto-devis-hero-start-btn').addEventListener('click', function () {
          heroEntered = true;
          setFlowActive(true);
          renderSearchStep();
        });
        return;
      }

      // Desktop (ou mobile jamais "entré", cf. return ci-dessus) : bloc hero pré-flux inline.
      // Mobile déjà "entré" une fois : reste en plein écran même en revenant à cette étape.
      setFlowActive(heroEntered);
      setStep(1, null);
      body.innerHTML =
        '<div class="mauto-devis-searchbar">' +
          '<div class="mauto-devis-search-wrap">' +
            '<input type="text" class="mauto-devis-search-input" placeholder="Votre prestation…" autocomplete="off">' +
            '<div class="mauto-devis-suggestions" hidden></div>' +
          '</div>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary mauto-devis-search-btn">Faire un devis</button>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-search-error" hidden></p>';

      var input = body.querySelector('.mauto-devis-search-input');
      var list = body.querySelector('.mauto-devis-suggestions');
      var errorEl = body.querySelector('.mauto-devis-search-error');
      var suggestions = buildSuggestionList();

      function showSuggestions(query) {
        var q = normalize(query);
        var matches = q
          ? suggestions.filter(function (s) { return normalize(s.label).indexOf(q) !== -1; })
          : suggestions;
        if (!matches.length) { list.hidden = true; list.innerHTML = ''; return; }
        // Pas de troncature : la liste ne compte qu'une vingtaine d'entrées et le conteneur
        // défile déjà (.mauto-devis-suggestions, max-height + overflow-y:auto) — un plafond à 8
        // masquait des prestations (ex. "Pneus") tant que le client n'avait pas encore tapé assez
        // de lettres pour les faire remonter dans les résultats filtrés.
        list.innerHTML = matches.map(function (s) {
          return '<button type="button" class="mauto-devis-suggestion" data-key="' + esc(s.key) + '">' + esc(s.label) + '</button>';
        }).join('');
        list.hidden = false;
        var items = list.querySelectorAll('.mauto-devis-suggestion');
        for (var i = 0; i < items.length; i++) {
          items[i].addEventListener('click', function (ev) {
            var key = ev.currentTarget.getAttribute('data-key');
            input.value = ev.currentTarget.textContent;
            list.hidden = true;
            errorEl.hidden = true;
            selectSuggestion(key);
          });
        }
      }

      function trySubmit() {
        var q = normalize(input.value);
        if (!q) {
          errorEl.textContent = 'Merci de saisir une prestation.';
          errorEl.hidden = false;
          return;
        }
        var match = suggestions.filter(function (s) { return normalize(s.label).indexOf(q) !== -1; })[0];
        if (!match) {
          errorEl.textContent = 'Prestation non reconnue — choisissez-la dans la liste suggérée.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        list.hidden = true;
        selectSuggestion(match.key);
      }

      input.addEventListener('input', function () { showSuggestions(input.value); });
      input.addEventListener('focus', function () { showSuggestions(input.value); });
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); trySubmit(); }
      });
      document.addEventListener('click', function (ev) {
        if (!body.contains(ev.target)) list.hidden = true;
      });
      body.querySelector('.mauto-devis-search-btn').addEventListener('click', trySubmit);
    }

    function renderStep1() {
      if (useSearchBar) renderSearchStep();
      else renderDropdownStep();
    }

    // "Autre (précisez)" : champ libre obligatoire affiché immédiatement à l'étape 1,
    // avant même de passer à l'étape Véhicule.
    function renderAutreStep() {
      setFlowActive(true); // décrire "Autre" = prestation déjà "choisie", plein écran mobile
      setStep(1, renderStep1);
      body.innerHTML =
        '<p class="mauto-devis-field-label">Décrivez la prestation souhaitée :</p>' +
        '<div class="mauto-devis-field">' +
          '<textarea id="mauto-devis-autre-desc" rows="3" placeholder="Ex. bruit suspect au freinage, voyant moteur allumé…" required>' + esc(state.autreDescription) + '</textarea>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-autre-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', renderStep1);
      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var desc = body.querySelector('#mauto-devis-autre-desc').value.trim();
        var errorEl = body.querySelector('.mauto-devis-autre-error');
        if (!desc) {
          errorEl.textContent = 'Merci de décrire la prestation souhaitée.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.autreDescription = desc;
        state.prestationKey = AUTRE_KEY;
        goBackFromVehicule = renderAutreStep;
        goToVehiculeOrEstimation();
      });
    }

    // Cartes côte à côte pour les familles à choix forcé (ex. climatisation) — pas de
    // pré-sélection automatique, l'utilisateur doit choisir explicitement un forfait.
    function renderFamilyChoice(familyKey, showBack) {
      setFlowActive(true);
      setStep(1, showBack ? renderStep1 : null);
      var keys = FAMILIES[familyKey];
      var cardsHtml = keys.map(function (k) {
        var p = state.pricing[k];
        if (!p) return '';
        return (
          '<button type="button" class="mauto-devis-forfait-card" data-key="' + esc(k) + '">' +
            '<span class="mauto-devis-forfait-label">' + esc(p.label) + '</span>' +
            '<span class="mauto-devis-forfait-prix">' + esc(formatPrixAffiche(p)) + '</span>' +
          '</button>'
        );
      }).join('');
      body.innerHTML =
        '<p class="mauto-devis-field-label">Choisissez la formule qui vous convient :</p>' +
        '<div class="mauto-devis-forfaits">' + cardsHtml + '</div>' +
        (showBack
          ? '<div class="mauto-devis-actions"><button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button></div>'
          : '');
      var cards = body.querySelectorAll('.mauto-devis-forfait-card');
      for (var i = 0; i < cards.length; i++) {
        cards[i].addEventListener('click', function (ev) {
          var k = ev.currentTarget.getAttribute('data-key');
          state.prestationKey = k;
          goBackFromVehicule = function () { renderFamilyChoice(familyKey, showBack); };
          goToVehiculeOrEstimation();
        });
      }
      var back = body.querySelector('[data-action="back"]');
      if (back) back.addEventListener('click', renderStep1);
    }

    function renderQuickPicksBar() {
      if (!quickPicksBar) return;
      quickPicksBar.innerHTML = '';
      quickPicks.forEach(function (k) {
        // "k" peut être une clé tarifaire directe (vidange) ou le nom d'une famille
        // (freinage, climatisation) — dans ce cas on affiche un libellé générique.
        var isFamilyPick = !!FAMILIES[k];
        var displayKey = isFamilyPick ? FAMILIES[k][0] : k;
        var p = state.pricing[displayKey];
        if (!p) return;
        var label = isFamilyPick ? (FAMILY_LABELS[k] || k) : p.label;
        var btn = el(
          '<button type="button" class="mauto-devis-quickpick" data-key="' + esc(k) + '">' +
            '<span class="mauto-devis-quickpick-icon">' + iconFor(k) + '</span>' +
            '<span class="mauto-devis-quickpick-label">' + esc(label) + '</span>' +
          '</button>'
        );
        btn.addEventListener('click', function () {
          selectSuggestion(k);
          var all = quickPicksBar.querySelectorAll('.mauto-devis-quickpick');
          for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
          btn.classList.add('is-active');
        });
        quickPicksBar.appendChild(btn);
      });
    }

    // Étape 2 — Véhicule : immatriculation/marque/modèle + chip de rappel de la prestation.
    // `p` est null quand cette étape est atteinte via validateCartOnly() pour un panier composé
    // uniquement de pneus (aucune prestation classique en cours) — dans ce cas pas de chip, et
    // "Continuer" saute directement aux Coordonnées plutôt qu'à l'Estimation (cf. plus bas).
    function renderStepVehicule() {
      setFlowActive(true);
      var p = state.prestationKey ? state.pricing[state.prestationKey] : null;

      // Sauvegarde ce qui est déjà tapé (même incomplet/invalide) avant de quitter l'étape en
      // arrière — la validation stricte reste appliquée au clic sur "Continuer". Définie avant
      // setStep() pour pouvoir servir à la fois à la flèche fixe mobile et au bouton inline.
      function captureVehiculeDraft() {
        state.vehicule = {
          immatriculation: body.querySelector('#mauto-devis-immatriculation').value,
          marque: body.querySelector('#mauto-devis-marque').value,
          modele: body.querySelector('#mauto-devis-modele').value,
        };
      }
      var backWithCapture = goBackFromVehicule ? function () { captureVehiculeDraft(); goBackFromVehicule(); } : null;
      setStep(2, backWithCapture);

      body.innerHTML =
        (p ?
          '<div class="mauto-devis-chip">' +
            '<span class="mauto-devis-chip-label">VOUS AVEZ SÉLECTIONNÉ&nbsp;: ' + esc(p.label) + '</span>' +
            (goBackFromVehicule ? '<button type="button" class="mauto-devis-chip-remove" aria-label="Changer de prestation">✕</button>' : '') +
          '</div>'
        : '') +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-immatriculation">Immatriculation *</label>' +
          '<input type="text" id="mauto-devis-immatriculation" name="immatriculation" placeholder="Ex. AB-123-CD" value="' + esc(state.vehicule.immatriculation) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-marque">Marque *</label>' +
          '<input type="text" id="mauto-devis-marque" name="marque" placeholder="Ex. Peugeot" value="' + esc(state.vehicule.marque) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-modele">Modèle *</label>' +
          '<input type="text" id="mauto-devis-modele" name="modele" placeholder="Ex. 208" value="' + esc(state.vehicule.modele) + '" required>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-vehicule-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          (goBackFromVehicule ? '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' : '') +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';

      var chipRemove = body.querySelector('.mauto-devis-chip-remove');
      if (chipRemove) chipRemove.addEventListener('click', backWithCapture);
      var back = body.querySelector('[data-action="back"]');
      if (back) back.addEventListener('click', backWithCapture);

      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var immatRaw = body.querySelector('#mauto-devis-immatriculation').value.trim();
        var marque = body.querySelector('#mauto-devis-marque').value.trim();
        var modele = body.querySelector('#mauto-devis-modele').value.trim();
        var errorEl = body.querySelector('.mauto-devis-vehicule-error');

        if (!immatRaw || !marque || !modele) {
          errorEl.textContent = "Merci de renseigner l'immatriculation, la marque et le modèle.";
          errorEl.hidden = false;
          return;
        }
        if (!IMMAT_REGEX.test(immatRaw)) {
          errorEl.textContent = "Format d'immatriculation invalide — utilisez le format AB-123-CD.";
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.vehicule = { immatriculation: normalizeImmat(immatRaw), marque: marque, modele: modele };
        goToStep(state.prestationKey ? 3 : 4);
      });
    }

    // Étape 3 — Estimation : prix selon le mode + premier créneau envisageable.
    function renderStepEstimation() {
      setFlowActive(true);
      var p = state.pricing[state.prestationKey];
      setStep(3, backFromEstimation);

      // sur_devis : pas de texte intermédiaire ni de bouton "Continuer" — la popup s'affiche
      // immédiatement, ses propres boutons remplacent "← Retour"/"Continuer".
      if (p.mode === 'sur_devis') {
        body.innerHTML = '<div class="mauto-devis-estimation"><p class="mauto-devis-prestation-label">' + esc(p.label) + '</p></div>';
        renderSurDevisModal();
        return;
      }

      var slot = formatDateLong(nextEligibleSlot());
      body.innerHTML =
        '<div class="mauto-devis-estimation">' +
          '<p class="mauto-devis-prestation-label">' + esc(p.label) + '</p>' +
          '<p class="mauto-devis-prix">' + esc(formatPrixAffiche(p)) + '</p>' +
          '<p class="mauto-devis-creneau">Premier créneau envisageable : <strong>' + esc(slot) + '</strong> (sous réserve de confirmation par le garage)</p>' +
          '<div class="mauto-devis-estimation-choice">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="rdv">Prendre rendez-vous</button>' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-secondary" data-action="devis">Demander un devis</button>' +
          '</div>' +
          '<p class="mauto-devis-estimation-choice-hint">« Prendre rendez-vous » réserve directement un créneau (toutes les prestations de votre panier, le cas échéant). « Demander un devis » vous permet d\'obtenir un chiffrage écrit avant de vous engager sur une date.</p>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost mauto-devis-btn-add" data-action="ajouter">➕ Ajouter une autre prestation</button>' +
          '<div class="mauto-devis-actions">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '</div>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', backFromEstimation);
      body.querySelector('[data-action="devis"]').addEventListener('click', function () { goToStep(4); });
      body.querySelector('[data-action="rdv"]').addEventListener('click', handoffToRdv);
      body.querySelector('[data-action="ajouter"]').addEventListener('click', addCurrentToCart);
    }

    // "Prendre rendez-vous" depuis l'étape Estimation (prix connu uniquement, cf. ci-dessus) :
    // passe la main entière au widget RDV dédié (devis-rdv-widget.js) plutôt que de recréer son
    // parcours ici — prestation(s) et véhicule sont déjà connus à ce stade (l'étape 2 Véhicule a
    // forcément été validée avant d'atteindre l'étape 3 pour la 1ère prestation du panier), il ne
    // reste que date/créneau/coordonnées à recueillir, ce que le widget RDV fait déjà tel quel.
    // `detail.prestations` est toujours un tableau désormais (longueur 1 = comportement identique
    // à l'ancien `detail.prestation` singulier ; ≥2 = panier, cf. devis-rdv-widget.js qui gère les
    // deux cas). Chaque libellé correspond à une entrée de RDV_PRESTATIONS (devis-rdv-widget.js) —
    // libellés copiés-collés depuis la même grille tarifaire serveur, cf. commentaire de ce
    // fichier. L'événement DOM (plutôt qu'un appel direct) garde ce widget agnostique de
    // l'existence d'un overlay RDV séparé — c'est devis-floating-widget.js qui sait ouvrir/fermer
    // les overlays et écoute cet événement.
    function handoffToRdv() {
      var items = state.cart.concat([{ prestationKey: state.prestationKey }]);
      var labels = items.map(cartItemLabel);
      document.dispatchEvent(new CustomEvent('mauto:open-rdv-widget', {
        detail: {
          prestations: labels,
          vehicule: {
            immatriculation: state.vehicule.immatriculation,
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
          },
        },
      }));
    }

    // Popup "non chiffrable en ligne" — s'affiche au clic sur "Continuer" à l'étape Estimation
    // quand la prestation est en mode sur_devis, avant d'avancer vers les coordonnées.
    function renderSurDevisModal() {
      var modal = el(
        '<div class="mauto-devis-modal-backdrop">' +
          '<div class="mauto-devis-modal">' +
            '<p class="mauto-devis-modal-title">Oups&nbsp;! Votre demande n\'est pas chiffrable en ligne</p>' +
            '<p class="mauto-devis-modal-text">Nous ne pouvons pas encore calculer votre devis en ligne pour cette prestation, mais nous y travaillons&nbsp;!</p>' +
            '<p class="mauto-devis-modal-help">Besoin d\'aide&nbsp;? <a href="tel:0240268661">02 40 26 86 61</a></p>' +
            '<div class="mauto-devis-modal-actions">' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="transmit">Transmettre ma demande au réparateur</button>' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost mauto-devis-btn-add" data-action="ajouter">➕ Ajouter une autre prestation</button>' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="modify">Modifier ma recherche</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
      container.appendChild(modal);
      modal.querySelector('[data-action="transmit"]').addEventListener('click', function () {
        modal.remove();
        goToStep(4);
      });
      modal.querySelector('[data-action="ajouter"]').addEventListener('click', function () {
        modal.remove();
        addCurrentToCart();
      });
      modal.querySelector('[data-action="modify"]').addEventListener('click', function () {
        modal.remove();
        renderStep1();
      });
    }

    // Montant numérique utilisé dans le total estimatif du panier : le plus bas connu (prix
    // fixe, ou borne basse d'une fourchette/"à partir de") — jamais un sur_devis. Logique ad.fr
    // (cf. leurs mentions légales sur le "montant total approximatif et estimatif"). Dupliqué
    // côté serveur (sync-server/server.js, /api/devis-groupe) qui recalcule et fait foi à l'email.
    function itemPrixNumerique(p) {
      if (!p) return null;
      if (p.mode === 'fixe' && typeof p.prix_fixe === 'number') return p.prix_fixe;
      if (p.mode === 'fourchette' && typeof p.min === 'number') return p.min;
      if (p.mode === 'a_partir_de' && typeof p.a_partir_de === 'number') return p.a_partir_de;
      return null;
    }

    // Panier complet = items déjà "ajoutés" + celui en cours de finalisation (sur lequel le
    // client vient de cliquer "Demander un devis"/"Transmettre ma demande"). Pas d'item courant
    // à ajouter quand state.prestationKey est vide (panier composé uniquement de pneus validé via
    // validateCartOnly()).
    function allCartItemsWithCurrent() {
      var items = state.cart.slice();
      if (state.prestationKey) {
        items.push({
          prestationKey: state.prestationKey,
          pneu: state.pneu,
          message: state.prestationKey === AUTRE_KEY ? state.autreDescription : '',
        });
      }
      return items;
    }

    // Récap multi-lignes + total estimatif affichés à l'étape Coordonnées quand le panier
    // contient au moins 2 prestations (item courant inclus). Les prestations sur-devis sont
    // listées mais explicitement exclues du total, avec la mention demandée.
    function cartRecapAndTotalHtml() {
      var items = allCartItemsWithCurrent();
      var rows = '';
      var total = 0;
      var hasChiffre = false;
      var hasSurDevis = false;
      items.forEach(function (item) {
        if (item.type === 'pneu_produit') {
          var montantPneu = pneuProduitMontant(item);
          total += montantPneu;
          hasChiffre = true;
          rows += '<li>' + esc(cartItemLabel(item)) + ' × ' + esc(item.quantite) + ' — ' + esc(formatEuro(montantPneu)) + '</li>';
          return;
        }
        var p = item.prestationKey === AUTRE_KEY ? AUTRE_PRESTATION : state.pricing[item.prestationKey];
        if (!p) return;
        if (p.mode === 'sur_devis') {
          hasSurDevis = true;
          rows += '<li>' + esc(p.label) + ' — prix communiqué après diagnostic, non inclus dans le total ci-dessous</li>';
        } else {
          var montant = itemPrixNumerique(p);
          if (montant != null) { total += montant; hasChiffre = true; }
          rows += '<li>' + esc(p.label) + ' — ' + esc(formatPrixAffiche(p)) + '</li>';
        }
      });
      var totalHtml = hasChiffre
        ? '<p class="mauto-devis-cart-total">Total estimatif TTC : <strong>' + esc(formatEuro(total)) + '</strong></p>' +
          '<p class="mauto-devis-cart-total-note">Cette estimation additionne les montants connus ci-dessus' + (hasSurDevis ? ' (hors prestations sur devis, dont le prix sera communiqué après diagnostic)' : '') + ' — ce n\'est pas un engagement ferme.</p>'
        : '';
      return '<ul class="mauto-devis-cart-recap-list">' + rows + '</ul>' + totalHtml;
    }

    // Étape 4 — Coordonnées : nom/téléphone/email + bouton final. N'est jamais atteinte que via
    // le choix explicite "Demander un devis" (prix connu) ou "Transmettre ma demande au
    // réparateur" (sur_devis) — jamais via "Prendre rendez-vous", qui bifurque entièrement vers
    // le widget RDV avant cette étape (cf. handoffToRdv). Le libellé est donc toujours "devis".
    function renderStepCoordonnees() {
      setFlowActive(true);
      var p = state.prestationKey ? state.pricing[state.prestationKey] : null;
      var isGroupe = state.cart.length > 0;
      var submitLabel = isGroupe ? 'Demander mon devis groupé' : 'Demander un devis';
      var recapHtml = isGroupe
        ? cartRecapAndTotalHtml()
        : '<p class="mauto-devis-recap">' + esc(p.label) + ' — ' + esc(formatPrixAffiche(p)) + '</p>';

      // Sauvegarde ce qui est déjà tapé (même incomplet) avant de revenir à l'estimation — la
      // validation stricte reste appliquée à la soumission finale.
      function saveCoordDraft() {
        state.coordonnees = {
          nom: body.querySelector('#mauto-devis-nom').value,
          telephone: body.querySelector('#mauto-devis-telephone').value,
          email: body.querySelector('#mauto-devis-email').value,
        };
      }
      // Panier composé uniquement de pneus (validé via validateCartOnly()) : pas d'Estimation à
      // revoir (jamais atteinte pour ce cas), retour au choix de prestation à la place.
      var backHandler = state.prestationKey
        ? function () { saveCoordDraft(); goToStep(3); }
        : function () { saveCoordDraft(); goToStep(1); };
      setStep(4, backHandler);

      body.innerHTML =
        '<form class="mauto-devis-form">' +
          recapHtml +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-nom">Nom *</label>' +
            '<input type="text" id="mauto-devis-nom" name="nom" value="' + esc(state.coordonnees.nom) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-telephone">Téléphone *</label>' +
            '<input type="tel" id="mauto-devis-telephone" name="telephone" value="' + esc(state.coordonnees.telephone) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-email">Email *</label>' +
            '<input type="email" id="mauto-devis-email" name="email" value="' + esc(state.coordonnees.email) + '" required>' +
          '</div>' +
          '<p class="mauto-devis-error mauto-devis-form-error" hidden></p>' +
          '<div class="mauto-devis-actions">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
            '<button type="submit" class="mauto-devis-btn mauto-devis-btn-primary">' + esc(submitLabel) + '</button>' +
          '</div>' +
        '</form>';
      body.querySelector('[data-action="back"]').addEventListener('click', backHandler);
      body.querySelector('form').addEventListener('submit', onSubmit);
    }

    function renderDone() {
      body.innerHTML =
        '<div class="mauto-devis-done">' +
          '<p class="mauto-devis-done-title">✅ Votre demande de devis a bien été envoyée</p>' +
          '<p>Un email de confirmation vient de vous être adressé. Nous revenons vers vous sous 24h ouvrées avec un devis chiffré.</p>' +
        '</div>';
    }

    function onSubmit(ev) {
      ev.preventDefault();
      var form = ev.target;
      var errorEl = form.querySelector('.mauto-devis-form-error');
      errorEl.hidden = true;

      var nom = form.nom.value.trim();
      var telephone = form.telephone.value.trim();
      var email = form.email.value.trim();

      if (!nom || !telephone || !email) {
        errorEl.textContent = 'Merci de renseigner votre nom, téléphone et email.';
        errorEl.hidden = false;
        return;
      }
      state.coordonnees = { nom: nom, telephone: telephone, email: email };

      var submitBtn = form.querySelector('button[type="submit"]');
      var originalLabel = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Envoi…';

      // Panier vide = comportement mono-prestation strictement inchangé (même payload, même
      // endpoint). Panier non vide = devis groupé : la prestation en cours de finalisation
      // rejoint les items déjà ajoutés dans un seul tableau `prestations`.
      var isGroupe = state.cart.length > 0;
      var payload, endpoint;
      if (isGroupe) {
        payload = {
          prestations: allCartItemsWithCurrent().map(function (item) {
            if (item.type === 'pneu_produit') {
              return {
                type: 'pneu_produit',
                productId: item.productId,
                productHandle: item.productHandle,
                marque: item.marque,
                modele: item.modele,
                dimension: item.dimension,
                saison: item.saison,
                quantite: item.quantite,
                prixPneuUnitaire: item.prixPneuUnitaire,
                prixMontageUnitaire: item.prixMontageUnitaire,
              };
            }
            return { prestationKey: item.prestationKey, pneu: item.pneu, message: item.message };
          }),
          vehicule: {
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
            immatriculation: state.vehicule.immatriculation,
          },
          nom: nom,
          telephone: telephone,
          email: email,
          source: presetKey ? 'page-prestation' : 'page-devis',
        };
        endpoint = '/api/devis-groupe';
      } else {
        payload = {
          prestationKey: state.prestationKey,
          vehicule: {
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
            immatriculation: state.vehicule.immatriculation,
          },
          pneu: state.pneu,
          nom: nom,
          telephone: telephone,
          email: email,
          message: state.prestationKey === AUTRE_KEY ? state.autreDescription : '',
          source: presetKey ? 'page-prestation' : 'page-devis',
        };
        endpoint = '/api/devis';
      }

      fetch(apiBase + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || !res.body.ok) throw new Error((res.body && res.body.error) || 'Erreur serveur');
          renderDone();
        })
        .catch(function (err) {
          errorEl.textContent = "Une erreur est survenue lors de l'envoi. Merci de réessayer ou de nous appeler directement.";
          errorEl.hidden = false;
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        });
    }

    renderLoading();
    fetch(apiBase + '/api/pricing')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.ok) throw new Error(data.error || 'Erreur tarifs');
        state.pricing = data.pricing;
        // Ajoutée uniquement ici (jamais dans FAMILIES ni dans les clés fournies par le
        // serveur) : n'apparaît donc que dans les listes dérivées de Object.keys(state.pricing)
        // — barre de recherche et menu déroulant complet — jamais dans les dropdowns de famille
        // scopée ni les icônes de raccourci.
        state.pricing[AUTRE_KEY] = AUTRE_PRESTATION;
        renderQuickPicksBar();
        if (isSingle) {
          goBackFromVehicule = null;
          state.prestationKey = presetKey;
          if (presetKey === DEVIS_PNEU_KEY) startPneuFlow(function () { goToStep(2); });
          else goToStep(2);
        } else if (isFamily && FORCE_CHOICE[presetKey]) {
          renderFamilyChoice(presetKey, false);
        } else if (isFamily) {
          goBackFromVehicule = renderStep1;
          state.prestationKey = FAMILIES[presetKey][0];
          goToStep(2);
        } else {
          renderStep1();
        }
      })
      .catch(function () {
        renderError('Impossible de charger les tarifs pour le moment. Merci de réessayer plus tard ou de nous appeler directement.');
      });
  }

  // Badge Google (bloc hero) : reflète en direct la note affichée par le widget d'avis Google
  // déjà installé sur le site (app Reputon — badge flottant présent sur toutes les pages, cf.
  // éléments .reputon-rating-count / .reputon-reviews-count). Reputon ne fournit pas d'API
  // publique, donc pas de nouvelle intégration ici : on lit simplement le texte déjà rendu par
  // Reputon sur la même page, avec quelques tentatives car Reputon charge sa donnée de façon
  // asynchrone après le rendu initial. Si Reputon est absent ou n'a pas encore répondu après
  // ~8s, le texte codé en dur du réglage de section (repli) reste affiché tel quel.
  function syncGoogleBadge() {
    var badgeTextEls = document.querySelectorAll('.mauto-devis-hero__badgetext');
    if (!badgeTextEls.length) return;

    function applyFromReputon() {
      var rateEl = document.querySelector('.reputon-rating-count');
      var countEl = document.querySelector('.reputon-reviews-count');
      var rating = rateEl && rateEl.textContent.trim();
      var countMatch = countEl && /\d+/.exec(countEl.textContent);
      if (!rating || !countMatch) return false;
      var text = rating.replace('.', ',') + '/5 sur ' + countMatch[0] + ' avis Google';
      for (var i = 0; i < badgeTextEls.length; i++) badgeTextEls[i].textContent = text;
      return true;
    }

    if (applyFromReputon()) return;
    var attempts = 0;
    var interval = setInterval(function () {
      attempts++;
      if (applyFromReputon() || attempts >= 16) clearInterval(interval);
    }, 500);
  }

  function init() {
    // [data-mauto-devis-lazy] : conteneur monté à la demande (ex. overlay du bouton flottant),
    // jamais initialisé automatiquement au chargement — cf. devis-floating-widget.js qui appelle
    // window.MautoDevisWidget.initWidget(container) lui-même à l'ouverture.
    var containers = document.querySelectorAll('[data-mauto-devis-widget]:not([data-mauto-devis-lazy])');
    for (var i = 0; i < containers.length; i++) initWidget(containers[i]);
    syncGoogleBadge();
  }

  // Exposée pour devis-floating-widget.js (montage à la demande du widget dans l'overlay).
  window.MautoDevisWidget = { initWidget: initWidget };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
